import { MotionConfig } from "framer-motion";
import { useEffect, lazy, Suspense, useState } from "react";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import { setPageMeta, injectJsonLd, removeJsonLd, SITE_URL, SITE_NAME, PHONE_INTL, breadcrumbSchema } from "@/lib/seo";
import { getKeywordsPool, getTopKeywordsForMeta } from "@/lib/keywords-pool";

const HomeSections = lazy(() => import("@/components/home/HomeLazy"));

const SectionFallback = () => (
  <div className="py-20" aria-hidden="true" />
);

const Index = () => {
  const [showDeferredSections, setShowDeferredSections] = useState(false);

  useEffect(() => {
    setPageMeta(
      "Dépannage Informatique Béziers 24h/24 7j/7 — PC sur mesure & Web | SUDWEB-TECH",
      "Dépannage informatique 24h/24 7j/7 à Béziers et 50 km : PC fixe dès 580€, sites web dès 480€. Diagnostic 75€, devis gratuit, jours fériés inclus.",
      `${SITE_URL}/`
    );
    injectJsonLd("breadcrumb-home", breadcrumbSchema([
      { name: "Accueil", url: SITE_URL },
    ]));
    injectJsonLd("website-schema", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": SITE_NAME,
      "url": SITE_URL,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${SITE_URL}/blog?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });
    injectJsonLd("local-business", {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#business`,
      "name": SITE_NAME,
      "image": `${SITE_URL}/pwa-icon-512.png`,
      "url": SITE_URL,
      "telephone": PHONE_INTL,
      "priceRange": "€€",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Vendres",
        "postalCode": "34350",
        "addressLocality": "Vendres",
        "addressRegion": "Hérault",
        "addressCountry": "FR",
      },
      "geo": { "@type": "GeoCoordinates", "latitude": 43.3441, "longitude": 3.2159 },
      "openingHoursSpecification": [
        { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday","PublicHolidays"], "opens": "00:00", "closes": "23:59" },
      ],
      "areaServed": [
        { "@type": "City", "name": "Béziers" },
        { "@type": "City", "name": "Vias" },
        { "@type": "City", "name": "Agde" },
        { "@type": "City", "name": "Cap d'Agde" },
        { "@type": "City", "name": "Pézenas" },
        { "@type": "City", "name": "Narbonne" },
        { "@type": "City", "name": "Sète" },
        { "@type": "City", "name": "Montpellier" },
      ],
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "50" },
      "sameAs": [],
      "keywords": getKeywordsPool(),
    });
    // Met à jour la meta keywords avec le top stratégique
    let metaKw = document.querySelector('meta[name="keywords"]');
    if (!metaKw) {
      metaKw = document.createElement("meta");
      metaKw.setAttribute("name", "keywords");
      document.head.appendChild(metaKw);
    }
    metaKw.setAttribute("content", getTopKeywordsForMeta());
    injectJsonLd("services-schema", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": [
        { "@type": "Service", "position": 1, "name": "PC Sur Mesure", "url": `${SITE_URL}/pc-sur-mesure`, "offers": { "@type": "Offer", "priceCurrency": "EUR", "price": "580" } },
        { "@type": "Service", "position": 2, "name": "Création de site web", "url": `${SITE_URL}/creation-web`, "offers": { "@type": "Offer", "priceCurrency": "EUR", "price": "480" } },
        { "@type": "Service", "position": 3, "name": "Dépannage informatique", "url": `${SITE_URL}/contact`, "offers": { "@type": "Offer", "priceCurrency": "EUR", "price": "49" } },
      ],
    });
    injectJsonLd("faq-home", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Qui contacter pour un dépannage informatique à Béziers ?",
          "acceptedAnswer": { "@type": "Answer", "text": "SUDWEB-TECH intervient à domicile à Béziers et dans tout l'Hérault, 24h/24 et 7j/7, jours fériés inclus. Diagnostic complet 75€, devis gratuit. Téléphone : 06 69 26 04 92." },
        },
        {
          "@type": "Question",
          "name": "Y a-t-il un dépannage informatique ouvert la nuit et les jours fériés à Béziers ?",
          "acceptedAnswer": { "@type": "Answer", "text": "Oui, SUDWEB-TECH est ouvert 24h/24, 7j/7, jours fériés inclus, pour tous les dépannages informatiques urgents à Béziers et dans un rayon de 50 km." },
        },
        {
          "@type": "Question",
          "name": "Combien coûte la création d'un site web à Béziers ?",
          "acceptedAnswer": { "@type": "Answer", "text": "SUDWEB-TECH crée des sites vitrines dès 590€ et des sites e-commerce dès 1490€, avec SEO local inclus et propriété complète du code." },
        },
        {
          "@type": "Question",
          "name": "Où acheter un PC sur mesure ou un PC gaming près de Béziers ?",
          "acceptedAnswer": { "@type": "Answer", "text": "SUDWEB-TECH assemble des PC fixe dès 580€ et des PC gaming dès 890€ (montage professionnel 150€, garantie 2 ans pièces et main d'œuvre), avec un configurateur en ligne." },
        },
        {
          "@type": "Question",
          "name": "Mon PC est très lent, que propose SUDWEB-TECH ?",
          "acceptedAnswer": { "@type": "Answer", "text": "SUDWEB-TECH réalise un diagnostic complet (75€) puis optimise votre PC : nettoyage, remplacement par un SSD NVMe, ajout de RAM ou réinstallation Windows propre avec conservation de vos données. Un PC lent redevient rapide pour bien moins cher qu'un achat neuf." },
        },
      ],
    });
    return () => {
      removeJsonLd("breadcrumb-home");
      removeJsonLd("website-schema");
      removeJsonLd("local-business");
      removeJsonLd("services-schema");
      removeJsonLd("faq-home");
    };
  }, []);

  useEffect(() => {
    const timer = globalThis.setTimeout(() => setShowDeferredSections(true), 160);
    return () => globalThis.clearTimeout(timer);
  }, []);

  return (
    <MotionConfig reducedMotion="user">
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Navbar />
      <main>
        <HeroSection />
        {showDeferredSections && (
          <Suspense fallback={<SectionFallback />}>
            <HomeSections />
          </Suspense>
        )}
      </main>
      <Footer />
    </div>
    </MotionConfig>
  );
};

export default Index;
